const ANCESTOR_DEPTH = 3;
const MS_TRANSLATOR_ATTRIBUTE = "_msttexthash";

export type SafeDomGuardMethod = "insertBefore" | "removeChild";

export type TranslationMarkers = {
  documentElementClassName: string;
  fontWrapperCount: number;
  hasMsTranslatorMarker: boolean;
};

export type SafeDomGuardEvidence = {
  ancestors: Array<string>;
  chainHasFontWrapper: boolean;
  chainHasMsTranslatorMarker: boolean;
  nodeName: string;
  textLength: number;
  translation: TranslationMarkers;
};

type MaybeElement = {
  className?: unknown;
  getAttribute?: (name: string) => string | null;
  nodeName?: string;
  parentNode?: MaybeElement | null;
  textContent?: string | null;
};

const isFontWrapper = (node: MaybeElement | null) => node?.nodeName === "FONT";

const hasMsTranslatorMarker = (node: MaybeElement | null) =>
  typeof node?.getAttribute === "function" &&
  node.getAttribute(MS_TRANSLATOR_ATTRIBUTE) !== null;

const describeNode = (node: MaybeElement) => {
  const name = node.nodeName ?? "#unknown";
  const className =
    typeof node.className === "string" ? node.className.trim() : "";
  return className ? `${name}.${className.split(/\s+/).join(".")}` : name;
};

export const readTranslationMarkers = (): TranslationMarkers => {
  if (typeof document === "undefined")
    return {
      documentElementClassName: "",
      fontWrapperCount: 0,
      hasMsTranslatorMarker: false,
    };

  return {
    documentElementClassName: document.documentElement?.className ?? "",
    fontWrapperCount: document.getElementsByTagName("font").length,
    hasMsTranslatorMarker:
      document.querySelector(`font[${MS_TRANSLATOR_ATTRIBUTE}]`) !== null,
  };
};

export const collectSafeDomGuardEvidence = (
  node: MaybeElement | null,
): SafeDomGuardEvidence => {
  const ancestors: Array<string> = [];
  let chainHasFontWrapper = isFontWrapper(node);
  let chainHasMsTranslatorMarker = hasMsTranslatorMarker(node);

  let ancestor = node?.parentNode;
  for (let depth = 0; depth < ANCESTOR_DEPTH && ancestor; depth++) {
    ancestors.push(describeNode(ancestor));
    chainHasFontWrapper = chainHasFontWrapper || isFontWrapper(ancestor);
    chainHasMsTranslatorMarker =
      chainHasMsTranslatorMarker || hasMsTranslatorMarker(ancestor);
    ancestor = ancestor.parentNode;
  }

  return {
    ancestors,
    chainHasFontWrapper,
    chainHasMsTranslatorMarker,
    nodeName: node?.nodeName ?? "#unknown",
    textLength: (node?.textContent ?? "").length,
    translation: readTranslationMarkers(),
  };
};

let installed = false;

export const installSafeDomGuards = (
  onViolation: (
    method: SafeDomGuardMethod,
    collectEvidence: () => SafeDomGuardEvidence,
  ) => void,
) => {
  if (installed || typeof globalThis.Node === "undefined") return;
  installed = true;

  const prototype = globalThis.Node.prototype;
  const nativeRemoveChild = prototype.removeChild;
  const nativeInsertBefore = prototype.insertBefore;

  const report = (method: SafeDomGuardMethod, node: MaybeElement) => {
    try {
      onViolation(method, () => collectSafeDomGuardEvidence(node));
    } catch {
      // reporting must never become the crash it is here to prevent
    }
  };

  prototype.removeChild = function removeChild<T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child && child.parentNode !== this) {
      report("removeChild", child);
      return child;
    }
    return nativeRemoveChild.call(this, child);
  };

  prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    node: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      report("insertBefore", referenceNode);
      return node;
    }
    return nativeInsertBefore.call(this, node, referenceNode);
  };
};
